import {
  createCartSync,
  type CartSync,
  type CartSyncDeps,
  type PendingMutation,
  type MutationRejection,
  type SyncReason,
} from './cartSync';

const PREFIX = 'btr-cart-v2:pending:';
const SIGNAL = 'btr-cart-v2:signal';
const REJECTED = 'btr-cart-v2:rejected:';
export const CART_SESSION_EVENT = 'btr-cart-session';

export function createBrowserCartSync<T extends { owner_context: string }>(
  deps: Pick<
    CartSyncDeps<T>,
    'getCart' | 'mutateCart' | 'applySnapshot' | 'onSyncState'
  >,
): CartSync {
  let channel: BroadcastChannel | null = null,
    listening = false;
  const tabId = crypto.randomUUID();
  function sessionChanged() {
    window.dispatchEvent(new Event(CART_SESSION_EVENT));
  }
  const sync = createCartSync({
    ...deps,
    tabId,
    now: Date.now,
    createMutationId: () => crypto.randomUUID(),
    readPending() {
      const result: PendingMutation[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(PREFIX)) continue;
        const value: PendingMutation = JSON.parse(localStorage.getItem(key)!);
        if (
          !value?.mutation?.mutation_id ||
          !value.mutation.owner_context ||
          !value.mutation.operation ||
          !Number.isFinite(value.createdAt)
        ) {
          throw new Error('Invalid cart queue');
        }
        result.push(value);
      }
      return result;
    },
    putPending(value) {
      localStorage.setItem(
        PREFIX + value.mutation.mutation_id,
        JSON.stringify(value),
      );
    },
    removePending(id) {
      localStorage.removeItem(PREFIX + id);
    },
    recordRejection(rejection) {
      // One latest rejection per initiating tab; expire records of closed tabs.
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key?.startsWith(REJECTED)) {
          const saved = JSON.parse(localStorage.getItem(key)!);
          if (saved.expiresAt < Date.now()) localStorage.removeItem(key);
        }
      }
      localStorage.setItem(
        REJECTED + rejection.tabId,
        JSON.stringify({
          ...rejection,
          expiresAt: Date.now() + 86400000,
        }),
      );
    },
    takeRejection() {
      const key = REJECTED + tabId;
      const value = localStorage.getItem(key);
      if (!value) return null;
      const rejection: MutationRejection = JSON.parse(value);
      localStorage.removeItem(key);
      return rejection;
    },
    async exclusive(work) {
      if (!navigator.locks)
        return Promise.reject(new Error('Web Locks unavailable'));
      // Test writes as well as reads; private mode can expose readable but unwritable storage.
      try {
        localStorage.setItem('btr-cart-v2:probe', tabId);
        localStorage.removeItem('btr-cart-v2:probe');
      } catch (error) {
        return Promise.reject(error);
      }
      return await navigator.locks.request('btr-cart-sync-v2', work);
    },
    publish(reason) {
      const signal = { reason, eventId: crypto.randomUUID() };
      if (channel) channel.postMessage(signal);
      else localStorage.setItem(SIGNAL, JSON.stringify(signal));
      if (reason === 'session') sessionChanged();
    },
  });
  function receive(value: unknown) {
    const reason = (value as { reason?: SyncReason })?.reason;
    if (reason !== 'cart' && reason !== 'session') return;
    sync.invalidate(reason);
    if (reason === 'session') sessionChanged();
  }
  function storage(event: StorageEvent) {
    if (event.key !== SIGNAL || !event.newValue) return;
    try {
      receive(JSON.parse(event.newValue));
    } catch {
      /* unrelated malformed signal */
    }
  }
  function visible() {
    if (document.visibilityState === 'visible') sync.refresh();
  }
  return {
    ...sync,
    start() {
      if (!listening) {
        listening = true;
        if (typeof BroadcastChannel !== 'undefined') {
          channel = new BroadcastChannel('btr-cart-v2');
          channel.onmessage = (event) => receive(event.data);
        }
        window.addEventListener('storage', storage);
        window.addEventListener('focus', sync.refresh);
        window.addEventListener('online', sync.refresh);
        document.addEventListener('visibilitychange', visible);
      }
      return sync.start();
    },
    stop() {
      listening = false;
      channel?.close();
      channel = null;
      window.removeEventListener('storage', storage);
      window.removeEventListener('focus', sync.refresh);
      window.removeEventListener('online', sync.refresh);
      document.removeEventListener('visibilitychange', visible);
      sync.stop();
    },
  };
}
